import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JOB_ROLES, EXPERIENCE_LEVELS, BRAND } from "@/lib/constants";
import {
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  MessageCircle,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import EasyApplyModal from "@/components/careers/EasyApplyModal";
import useSeo from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CareersPage() {
  useSeo({
    title:
      "Careers at InvitationWala | Design Studio Jobs for Wedding Invite Creators",
    description:
      "Join InvitationWala — a premium digital wedding invitation atelier hiring designers, motion artists & strategists. Easy Apply on WhatsApp in 30 seconds.",
    canonical: "https://www.invitationwala.online/careers",
  });
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: "",
    experience: "",
    portfolio_url: "",
    cover_note: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [last, setLast] = useState(null);
  const [easyRole, setEasyRole] = useState(null); // Easy Apply modal state

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickRole = (title) => {
    update("role", title);
    setTimeout(() => {
      const el = document.getElementById("apply");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.role || !form.experience) {
      toast.error("Please fill name, phone, email, role and experience.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/careers`, form);
      setLast({ ...form });
      setDone(true);
      toast.success("Application received — we'll reach out soon.");
      if (typeof window !== "undefined") {
        if (typeof window.fbq === "function") {
          window.fbq("trackCustom", "JobApplication", {
            role: form.role,
            experience: form.experience,
          });
        }
        if (typeof window.gtag === "function") {
          window.gtag("event", "job_application", {
            event_category: "Careers",
            event_label: form.role,
          });
        }
      }
      setForm({
        name: "",
        phone: "",
        email: "",
        role: "",
        experience: "",
        portfolio_url: "",
        cover_note: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = () => {
    if (!last) return "#";
    const lines = [
      `Namaste ${BRAND.name} team,`,
      ``,
      `I just applied for a role —`,
      `• *Role:* ${last.role}`,
      `• *Name:* ${last.name}`,
      `• *Experience:* ${last.experience}`,
      `• *Phone:* ${last.phone}`,
      `• *Email:* ${last.email}`,
    ];
    if (last.portfolio_url) lines.push(`• *Portfolio:* ${last.portfolio_url}`);
    lines.push(``, `Looking forward to hearing from you.`);
    return `https://wa.me/${BRAND.hrWhatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        background:
          "radial-gradient(ellipse at 20% 0%, rgba(212,175,55,0.08) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(232,165,180,0.08) 0%, transparent 55%), #0a0a0a",
      }}
      data-testid="careers-page"
    >
      {/* JobPosting structured data for Google Jobs search */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": JOB_ROLES.map((r) => ({
              "@type": "JobPosting",
              "@id": `https://www.invitationwala.online/careers#${r.id}`,
              title: r.title,
              description: r.summary,
              datePosted: "2026-01-15",
              validThrough: "2026-12-31",
              employmentType: r.type.toLowerCase().includes("intern")
                ? "INTERN"
                : "FULL_TIME",
              hiringOrganization: {
                "@type": "Organization",
                name: "InvitationWala",
                sameAs: "https://www.invitationwala.online/",
                logo: "https://www.invitationwala.online/logos/invitation-wala.png",
              },
              jobLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Noida",
                  addressRegion: "Uttar Pradesh",
                  addressCountry: "IN",
                },
              },
              applicantLocationRequirements: {
                "@type": "Country",
                name: "India",
              },
              directApply: true,
              url: "https://www.invitationwala.online/careers",
            })),
          }),
        }}
      />
      {/* Top nav */}
      <header className="sticky top-0 z-30 px-5 md:px-10 py-4 flex items-center justify-between gap-4"
        style={{
          background: "rgba(10,10,10,0.8)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}>
        <Link
          to="/"
          className="inline-flex items-center gap-2 overline text-[var(--iw-faint)] hover:text-[var(--iw-gold)] transition-colors"
          data-testid="careers-back-link"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
        <a
          href={BRAND.instagramUrl}
          target="_blank"
          rel="noreferrer"
          className="overline text-[var(--iw-gold)]"
        >
          @{BRAND.instagram.toLowerCase()}
        </a>
      </header>

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <span className="overline text-[var(--iw-gold)]">
              Careers · The Atelier
            </span>
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
          </div>
          <h1
            className="font-serif-display text-[var(--iw-ivory)] text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.92] tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Design the way
            <br />
            <span className="italic gold-rose-gradient">India</span> gets
            married.
          </h1>
          <p className="text-[var(--iw-ivory-dim)] text-base md:text-lg leading-relaxed max-w-xl mx-auto mt-8 font-light">
            We're a quiet atelier crafting the most shared wedding invitations
            in the country. If you love type, motion, stories and beautiful
            detail — we'd love to meet you.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setEasyRole("")}
              className="btn-gold inline-flex items-center gap-2"
              data-testid="career-hero-easy-apply"
            >
              <Zap size={14} fill="currentColor" />
              Easy Apply · 30 sec
            </button>
            <a
              href="#apply"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("apply");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-ghost-gold text-xs md:text-sm"
              data-testid="career-hero-detailed"
            >
              Detailed application
            </a>
          </div>
        </motion.div>
      </section>

      {/* Perks strip */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-12 pb-10 md:pb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { k: "Creative", v: "Work on celebrity + creator weddings" },
            { k: "Premium", v: "Only luxury clientele, no race-to-bottom" },
            { k: "Flexible", v: "Hybrid. Outcome over hours" },
            { k: "Growth", v: "Direct mentorship from the founder" },
          ].map((p) => (
            <div
              key={p.k}
              className="p-4 md:p-5 border border-white/8"
              style={{ background: "rgba(255,255,255,0.02)" }}
            >
              <div className="overline text-[var(--iw-gold)] mb-2">{p.k}</div>
              <div className="text-[var(--iw-ivory-dim)] text-sm leading-snug">
                {p.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Openings */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-12 pb-20 md:pb-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-8 mb-8 md:mb-12">
          <div>
            <div className="overline text-[var(--iw-gold)] mb-4">
              Open Positions
            </div>
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-5xl leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Roles we're <span className="italic">hiring for</span>.
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setEasyRole("")}
            className="btn-ghost-gold self-start md:self-auto text-xs md:text-sm"
            data-testid="careers-jump-apply"
          >
            <Zap size={12} fill="currentColor" /> Easy Apply now
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-5">
          {JOB_ROLES.map((r, i) => (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.8,
                delay: (i % 2) * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group p-6 md:p-7 flex flex-col gap-4 transition-colors"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(212,175,55,0.18)",
              }}
              data-testid={`career-role-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3
                    className="font-serif-display text-[var(--iw-ivory)] text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {r.title}
                  </h3>
                  <div className="overline text-[var(--iw-faint)] mt-2">
                    {r.type}
                  </div>
                </div>
                <Sparkles
                  size={16}
                  strokeWidth={1.25}
                  className="text-[var(--iw-gold)] shrink-0 mt-1 opacity-60 group-hover:opacity-100 transition-opacity"
                />
              </div>
              <p className="text-[var(--iw-ivory-dim)] text-sm leading-relaxed">
                {r.summary}
              </p>
              <div className="flex flex-wrap gap-2 mt-1">
                {r.must.map((m) => (
                  <span
                    key={m}
                    className="overline text-[10px] px-2.5 py-1 border"
                    style={{
                      borderColor: "rgba(212,175,55,0.3)",
                      color: "var(--iw-gold)",
                      background: "rgba(212,175,55,0.06)",
                    }}
                  >
                    {m}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-1">
                <button
                  type="button"
                  onClick={() => setEasyRole(r.title)}
                  className="btn-gold inline-flex items-center gap-2"
                  data-testid={`career-easy-apply-${r.id}`}
                >
                  <Zap size={12} fill="currentColor" />
                  Easy Apply
                </button>
                <button
                  type="button"
                  onClick={() => pickRole(r.title)}
                  className="btn-ghost-gold text-xs"
                  data-testid={`career-detailed-apply-${r.id}`}
                >
                  Detailed form
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Apply form */}
      <section
        id="apply"
        className="relative py-16 md:py-28 border-t border-white/5"
        style={{
          background: "linear-gradient(180deg, #1a1414 0%, #1e1a16 100%)",
        }}
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-px bg-[var(--iw-gold)]" />
                <span className="overline text-[var(--iw-gold)]">
                  The Application
                </span>
              </div>
              <h2
                className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-5xl leading-[0.95] tracking-tight"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                Tell us about <span className="italic gold-rose-gradient">you</span>.
              </h2>
              <p className="text-[var(--iw-muted)] text-sm md:text-base leading-relaxed mt-6 max-w-md">
                A short form. If your craft resonates, we'll WhatsApp you within
                72 hours for a warm chat. Portfolio link is a big yes.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  { k: "01", v: "Warm WhatsApp call — no ATS black-hole." },
                  { k: "02", v: "Paid test brief, on a real (anonymised) project." },
                  { k: "03", v: "Meet the team over chai, see the studio." },
                ].map((m) => (
                  <div
                    key={m.k}
                    className="flex items-start gap-5 border-l border-[var(--iw-border-gold)] pl-5"
                  >
                    <span
                      className="font-serif-display italic text-[var(--iw-gold)] text-xl"
                      style={{ fontFamily: "Cormorant Garamond, serif" }}
                    >
                      {m.k}
                    </span>
                    <p className="text-[var(--iw-ivory-dim)] text-sm leading-relaxed">
                      {m.v}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <motion.form
                onSubmit={submit}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="glass-card p-6 md:p-10 relative"
                data-testid="careers-form"
              >
                {done && (
                  <div className="absolute inset-0 z-10 grid place-items-center bg-black/85 backdrop-blur-sm p-6 text-center">
                    <div className="max-w-sm">
                      <CheckCircle2
                        className="text-[var(--iw-gold)] mx-auto mb-5"
                        size={52}
                        strokeWidth={1}
                      />
                      <h3
                        className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
                        style={{ fontFamily: "Cormorant Garamond, serif" }}
                      >
                        Application{" "}
                        <span className="italic text-[var(--iw-gold)]">
                          received
                        </span>
                        .
                      </h3>
                      <p className="text-[var(--iw-muted)] mt-3 leading-relaxed text-sm">
                        We read every note personally. If your craft resonates,
                        expect a WhatsApp within 72 hours.
                      </p>
                      <a
                        href={waLink()}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-gold mt-6 w-full justify-center"
                        data-testid="careers-wa-send"
                      >
                        <MessageCircle size={13} />
                        Share on WhatsApp too
                      </a>
                      <button
                        onClick={() => setDone(false)}
                        className="btn-ghost-gold mt-3 w-full justify-center"
                        data-testid="careers-reset"
                      >
                        Submit Another
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                  <Field label="Full Name" required>
                    <input
                      type="text"
                      className="input-luxe"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your name"
                      data-testid="career-name"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      type="tel"
                      className="input-luxe"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+91"
                      data-testid="career-phone"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      type="email"
                      className="input-luxe"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      data-testid="career-email"
                    />
                  </Field>
                  <Field label="Applying for" required>
                    <Select
                      value={form.role}
                      onValueChange={(v) => update("role", v)}
                    >
                      <SelectTrigger
                        className="input-luxe h-[50px] rounded-none"
                        data-testid="career-role-trigger"
                      >
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent
                        className="border-white/15 rounded-none"
                        style={{
                          background: "#0a0a0a",
                          color: "var(--iw-ivory)",
                        }}
                      >
                        {JOB_ROLES.map((r) => (
                          <SelectItem
                            key={r.id}
                            value={r.title}
                            className="font-sans-body"
                            data-testid={`career-role-opt-${r.id}`}
                          >
                            {r.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Experience" required>
                    <Select
                      value={form.experience}
                      onValueChange={(v) => update("experience", v)}
                    >
                      <SelectTrigger
                        className="input-luxe h-[50px] rounded-none"
                        data-testid="career-experience-trigger"
                      >
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent
                        className="border-white/15 rounded-none"
                        style={{
                          background: "#0a0a0a",
                          color: "var(--iw-ivory)",
                        }}
                      >
                        {EXPERIENCE_LEVELS.map((x) => (
                          <SelectItem
                            key={x}
                            value={x}
                            className="font-sans-body"
                          >
                            {x}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Portfolio / Behance / Drive link">
                    <input
                      type="url"
                      className="input-luxe"
                      value={form.portfolio_url}
                      onChange={(e) => update("portfolio_url", e.target.value)}
                      placeholder="https://..."
                      data-testid="career-portfolio"
                    />
                  </Field>
                  <Field label="A short note (why us)" full>
                    <textarea
                      rows={4}
                      className="input-luxe resize-none"
                      value={form.cover_note}
                      onChange={(e) => update("cover_note", e.target.value)}
                      placeholder="One paragraph — what you've made, what you'd love to make here..."
                      data-testid="career-note"
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold mt-7 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="career-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Application
                      <Send size={14} />
                    </>
                  )}
                </button>
                <p className="overline text-[var(--iw-faint)] mt-4 flex items-center gap-2">
                  <Star size={10} className="text-[var(--iw-rose)]" fill="currentColor" />
                  We read every note · No auto-rejections
                </p>
              </motion.form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="px-5 md:px-12 py-8 border-t border-white/5 text-center">
        <Link
          to="/"
          className="overline text-[var(--iw-faint)] hover:text-[var(--iw-gold)] transition-colors"
        >
          ← Back to {BRAND.name}
        </Link>
      </footer>

      {/* Easy Apply Modal — 1-tap LinkedIn-style */}
      <EasyApplyModal
        open={easyRole !== null}
        onClose={() => setEasyRole(null)}
        role={easyRole || "General"}
      />
    </div>
  );
}

const Field = ({ label, children, required, full }) => (
  <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
    <span className="overline text-[var(--iw-faint)]">
      {label} {required && <span className="text-[var(--iw-gold)]">·</span>}
    </span>
    {children}
  </label>
);
